import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  GoogleAuthProvider, 
  signOut,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth';
import { getDatabase } from '@firebase/database';

// Validate environment variables
const validateEnvVariables = () => {
  const requiredVars = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
    'REACT_APP_FIREBASE_DATABASE_URL'
  ];

  const missingVars = requiredVars.filter(varName => !process.env[varName]);
  
  if (missingVars.length > 0) {
    console.error('Missing Firebase configuration variables:', missingVars);
    throw new Error('Firebase configuration is incomplete');
  }
};

// Validate environment variables before initializing Firebase
validateEnvVariables();

// Your Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// Log the configuration (remove sensitive info)
console.log('Firebase Config:', {
  apiKey: firebaseConfig.apiKey ? '[REDACTED]' : 'MISSING',
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  databaseURL: firebaseConfig.databaseURL
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Authentication
const auth = getAuth(app);
auth.useDeviceLanguage();

// Initialize Realtime Database
const database = getDatabase(app);

// Set persistence to LOCAL
setPersistence(auth, browserLocalPersistence)
  .catch((error) => {
    console.error("Auth persistence error:", error);
  });

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Authentication Methods
const firebaseSignUp = async (email, password, name) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, {
      displayName: name
    });
    return userCredential;
  } catch (error) {
    console.error('Signup error:', error);
    throw error;
  }
};

const firebaseLogin = async (email, password) => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

const firebaseGoogleSignIn = async () => {
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error) {
    console.error('Google signin error:', error);
    throw error;
  }
};

const firebaseLogout = async () => {
  try {
    return await signOut(auth);
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

// Phone Authentication Methods
const setupInvisibleRecaptcha = () => {
  const auth = getAuth();
  return new RecaptchaVerifier(auth, 'invisible-recaptcha', {
    'size': 'invisible',
    'callback': () => {
      console.log('reCAPTCHA verified');
    },
    'expired-callback': () => {
      console.log('reCAPTCHA expired');
    },
    'error-callback': (error) => {
      console.error('reCAPTCHA error:', error);
    },
    'isolated': true,
    'badge': 'bottomright',
    'theme': 'light',
    'type': 'image'
  });
};

const firebasePhoneSignIn = async (phoneNumber) => {
  try {
    const auth = getAuth();
    
    // Clear any existing reCAPTCHA
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
    }
    
    // Create new reCAPTCHA verifier
    const recaptchaVerifier = setupInvisibleRecaptcha();
    window.recaptchaVerifier = recaptchaVerifier;
    
    // Render the reCAPTCHA
    await recaptchaVerifier.render();
    
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    return confirmationResult;
  } catch (error) {
    console.error('Phone signin error:', error);
    // Clear reCAPTCHA on error
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
    throw error;
  }
};

const verifyOTP = async (confirmationResult, otp) => {
  try {
    const result = await confirmationResult.confirm(otp);
    // Clear reCAPTCHA after successful verification
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
    return result;
  } catch (error) {
    console.error('OTP verification error:', error);
    throw error;
  }
};

export { 
  app,
  auth, 
  database,
  googleProvider,
  firebaseSignUp,
  firebaseLogin,
  firebaseGoogleSignIn,
  firebaseLogout,
  firebasePhoneSignIn,
  verifyOTP
};